import React, { useState, useEffect } from "react";
import { Button as UIButton } from "@/components/ui/button";
import { TigolaLogo } from "@/components/TigolaLogo";
import { Card as UICard, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input as UIInput } from "@/components/ui/input";
import { Tabs as UITabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pound,
  Edit,
  Save,
  RefreshCw,
  MapPin,
  Plane,
  Car,
  Users,
  Crown,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Settings,
  Download,
  Upload,
} from "lucide-react";
import {
  generateLocationPricing,
  getPricingForLocation,
  updateLocationPricing,
  bulkUpdateRegionalPricing,
  formatPrice,
  getPricingSummary,
  type LocationPricing,
  type ServicePricing,
} from "@/utils/pricing-config";

const Card = UICard;
const Input = UIInput;
const Tabs = UITabs;
const Select = UISelect;
const Table = UITable;

export default function PricingManagement() {
  const [locationPricing, setLocationPricing] = useState<LocationPricing[]>([]);
  const [editingLocation, setEditingLocation] = useState<string | null>(null);
  const [bulkUpdateRegion, setBulkUpdateRegion] = useState<
    "Leicester" | "Birmingham" | null
  >(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState<
    "all" | "Leicester" | "Birmingham"
  >("all");
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  // Initialize pricing data
  useEffect(() => {
    const pricing = generateLocationPricing();
    setLocationPricing(pricing);
  }, []);

  // Filter locations
  const filteredLocations = locationPricing.filter((location) => {
    const matchesSearch =
      location.location_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      location.location_slug.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion =
      filterRegion === "all" || location.parent_city === filterRegion;
    return matchesSearch && matchesRegion;
  });

  // Group by region
  const leicesterLocations = filteredLocations.filter(
    (l) => l.parent_city === "Leicester",
  );
  const birminghamLocations = filteredLocations.filter(
    (l) => l.parent_city === "Birmingham",
  );

  // Handle individual price updates
  const updatePrice = (
    locationSlug: string,
    serviceType: keyof ServicePricing,
    newPrice: number,
  ) => {
    setLocationPricing((prev) =>
      prev.map((location) =>
        location.location_slug === locationSlug
          ? {
              ...location,
              services: { ...location.services, [serviceType]: newPrice },
              last_updated: new Date().toISOString(),
              updated_by: "admin",
            }
          : location,
      ),
    );
    setHasChanges(true);
  };

  // Handle bulk regional updates
  const handleBulkUpdate = (
    region: "Leicester" | "Birmingham",
    newPricing: Partial<ServicePricing>,
  ) => {
    setLocationPricing((prev) =>
      prev.map((location) =>
        location.parent_city === region
          ? {
              ...location,
              services: { ...location.services, ...newPricing },
              last_updated: new Date().toISOString(),
              updated_by: "admin",
            }
          : location,
      ),
    );
    setHasChanges(true);
    setBulkUpdateRegion(null);
  };

  // Save changes
  const saveChanges = async () => {
    setSaveStatus("saving");
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setHasChanges(false);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      setSaveStatus("error");
    }
  };

  // Get summary statistics
  const summary = getPricingSummary();

  const ServiceIcon = ({ service }: { service: keyof ServicePricing }) => {
    const icons = {
      airport_transfer: Plane,
      local_taxi: Car,
      group_transfer: Users,
      executive_service: Crown,
    };
    const Icon = icons[service];
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <a href="/" className="flex-shrink-0">
                <TigolaLogo className="h-12 w-auto" alt="Tigola Admin" />
              </a>
              <div className="ml-8">
                <h1 className="text-2xl font-bold text-gray-900">
                  Pricing Management
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {hasChanges && (
                <UIButton
                  onClick={saveChanges}
                  disabled={saveStatus === "saving"}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saveStatus === "saving" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : saveStatus === "saved" ? (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  {saveStatus === "saving"
                    ? "Saving..."
                    : saveStatus === "saved"
                      ? "Saved!"
                      : "Save Changes"}
                </UIButton>
              )}
              <UIButton asChild variant="outline">
                <a href="/analytics">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Analytics
                </a>
              </UIButton>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Overview */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900">
                  {summary.total_locations}
                </div>
                <div className="text-gray-600">Total Locations</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Plane className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900">
                  {formatPrice(summary.average_airport_transfer)}
                </div>
                <div className="text-gray-600">Avg Airport Transfer</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <Car className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900">
                  {formatPrice(summary.average_local_taxi)}
                </div>
                <div className="text-gray-600">Avg Local Taxi</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <TrendingUp className="h-8 w-8 text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900">
                  {formatPrice(summary.price_range.airport_transfer.min)}-
                  {formatPrice(summary.price_range.airport_transfer.max)}
                </div>
                <div className="text-gray-600">Price Range</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Filters & Bulk Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Input
                  placeholder="Search locations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <Select
                value={filterRegion}
                onValueChange={(value: any) => setFilterRegion(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  <SelectItem value="Leicester">Leicester Area</SelectItem>
                  <SelectItem value="Birmingham">Birmingham Area</SelectItem>
                </SelectContent>
              </Select>

              <UIButton
                variant="outline"
                onClick={() => setBulkUpdateRegion("Leicester")}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Bulk Update Leicester
              </UIButton>

              <UIButton
                variant="outline"
                onClick={() => setBulkUpdateRegion("Birmingham")}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Bulk Update Birmingham
              </UIButton>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="individual" className="space-y-6">
          <TabsList>
            <TabsTrigger value="individual">Individual Pricing</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Management</TabsTrigger>
            <TabsTrigger value="analytics">Pricing Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="individual">
            <div className="space-y-6">
              {/* Leicester Locations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Leicester Area ({leicesterLocations.length} locations)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <LocationPricingTable
                    locations={leicesterLocations}
                    onUpdatePrice={updatePrice}
                    editingLocation={editingLocation}
                    setEditingLocation={setEditingLocation}
                  />
                </CardContent>
              </Card>

              {/* Birmingham Locations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Birmingham Area ({birminghamLocations.length} locations)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <LocationPricingTable
                    locations={birminghamLocations}
                    onUpdatePrice={updatePrice}
                    editingLocation={editingLocation}
                    setEditingLocation={setEditingLocation}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="bulk">
            <BulkPricingManager onBulkUpdate={handleBulkUpdate} />
          </TabsContent>

          <TabsContent value="analytics">
            <PricingAnalytics locationPricing={locationPricing} />
          </TabsContent>
        </Tabs>

        {/* Bulk Update Modal */}
        {bulkUpdateRegion && (
          <BulkUpdateModal
            region={bulkUpdateRegion}
            onUpdate={handleBulkUpdate}
            onClose={() => setBulkUpdateRegion(null)}
          />
        )}
      </div>
    </div>
  );
}

// Location Pricing Table Component
function LocationPricingTable({
  locations,
  onUpdatePrice,
  editingLocation,
  setEditingLocation,
}: {
  locations: LocationPricing[];
  onUpdatePrice: (
    slug: string,
    service: keyof ServicePricing,
    price: number,
  ) => void;
  editingLocation: string | null;
  setEditingLocation: (slug: string | null) => void;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Location</TableHead>
          <TableHead>Airport Transfer</TableHead>
          <TableHead>Local Taxi</TableHead>
          <TableHead>Group Transfer</TableHead>
          <TableHead>Executive Service</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {locations.map((location) => (
          <TableRow key={location.location_slug}>
            <TableCell>
              <div>
                <div className="font-medium">{location.location_name}</div>
                <div className="text-sm text-gray-500">
                  {location.location_slug}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <PriceInput
                value={location.services.airport_transfer}
                isEditing={editingLocation === location.location_slug}
                onChange={(value) =>
                  onUpdatePrice(
                    location.location_slug,
                    "airport_transfer",
                    value,
                  )
                }
              />
            </TableCell>
            <TableCell>
              <PriceInput
                value={location.services.local_taxi}
                isEditing={editingLocation === location.location_slug}
                onChange={(value) =>
                  onUpdatePrice(location.location_slug, "local_taxi", value)
                }
              />
            </TableCell>
            <TableCell>
              <PriceInput
                value={location.services.group_transfer}
                isEditing={editingLocation === location.location_slug}
                onChange={(value) =>
                  onUpdatePrice(location.location_slug, "group_transfer", value)
                }
              />
            </TableCell>
            <TableCell>
              <PriceInput
                value={location.services.executive_service}
                isEditing={editingLocation === location.location_slug}
                onChange={(value) =>
                  onUpdatePrice(
                    location.location_slug,
                    "executive_service",
                    value,
                  )
                }
              />
            </TableCell>
            <TableCell>
              <UIButton
                variant="outline"
                size="sm"
                onClick={() =>
                  setEditingLocation(
                    editingLocation === location.location_slug
                      ? null
                      : location.location_slug,
                  )
                }
              >
                <Edit className="h-4 w-4" />
              </UIButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Price Input Component
function PriceInput({
  value,
  isEditing,
  onChange,
}: {
  value: number;
  isEditing: boolean;
  onChange: (value: number) => void;
}) {
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  if (!isEditing) {
    return <span className="font-medium">£{value}</span>;
  }

  return (
    <Input
      type="number"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onBlur={() => {
        const newValue = parseInt(inputValue) || value;
        onChange(newValue);
      }}
      className="w-20"
    />
  );
}

// Bulk Update Modal
function BulkUpdateModal({
  region,
  onUpdate,
  onClose,
}: {
  region: "Leicester" | "Birmingham";
  onUpdate: (
    region: "Leicester" | "Birmingham",
    pricing: Partial<ServicePricing>,
  ) => void;
  onClose: () => void;
}) {
  const [pricing, setPricing] = useState<Partial<ServicePricing>>({});

  const handleSubmit = () => {
    if (Object.keys(pricing).length > 0) {
      onUpdate(region, pricing);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Bulk Update {region} Pricing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Airport Transfer</label>
            <Input
              type="number"
              placeholder="Leave empty to skip"
              onChange={(e) =>
                setPricing((prev) => ({
                  ...prev,
                  airport_transfer: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium">Local Taxi</label>
            <Input
              type="number"
              placeholder="Leave empty to skip"
              onChange={(e) =>
                setPricing((prev) => ({
                  ...prev,
                  local_taxi: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium">Group Transfer</label>
            <Input
              type="number"
              placeholder="Leave empty to skip"
              onChange={(e) =>
                setPricing((prev) => ({
                  ...prev,
                  group_transfer: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                }))
              }
            />
          </div>
          <div>
            <label className="text-sm font-medium">Executive Service</label>
            <Input
              type="number"
              placeholder="Leave empty to skip"
              onChange={(e) =>
                setPricing((prev) => ({
                  ...prev,
                  executive_service: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                }))
              }
            />
          </div>
          <div className="flex gap-2">
            <UIButton onClick={handleSubmit} className="flex-1">
              Update All
            </UIButton>
            <UIButton variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </UIButton>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Bulk Pricing Manager Component
function BulkPricingManager({
  onBulkUpdate,
}: {
  onBulkUpdate: (
    region: "Leicester" | "Birmingham",
    pricing: Partial<ServicePricing>,
  ) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Leicester Area Bulk Update</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Update pricing for all Leicester area locations at once.
          </p>
          <UIButton onClick={() => {}}>Configure Leicester Pricing</UIButton>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Birmingham Area Bulk Update</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-4">
            Update pricing for all Birmingham area locations at once.
          </p>
          <UIButton onClick={() => {}}>Configure Birmingham Pricing</UIButton>
        </CardContent>
      </Card>
    </div>
  );
}

// Pricing Analytics Component
function PricingAnalytics({
  locationPricing,
}: {
  locationPricing: LocationPricing[];
}) {
  const summary = getPricingSummary();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Price Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between">
                <span>Airport Transfer</span>
                <span>
                  {formatPrice(summary.price_range.airport_transfer.min)} -{" "}
                  {formatPrice(summary.price_range.airport_transfer.max)}
                </span>
              </div>
            </div>
            <div>
              <div className="flex justify-between">
                <span>Local Taxi</span>
                <span>
                  {formatPrice(summary.price_range.local_taxi.min)} -{" "}
                  {formatPrice(summary.price_range.local_taxi.max)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Regional Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between">
                <span>Leicester Locations</span>
                <span>{summary.leicester_locations}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between">
                <span>Birmingham Locations</span>
                <span>{summary.birmingham_locations}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
